import { DateTime } from 'luxon';

/**
 * Formats an ISO date string into a medium date format (e.g. "Oct 14, 1983").
 *
 * @param {string|null|undefined} date - ISO 8601 date string.
 * @param {string} [_timezone] - Unused timezone parameter.
 * @returns {string} Formatted date string or '(not provided)'.
 */
export function formatDate(date, _timezone) {
  if (date) {
    // return DateTime.fromISO(date).setZone(timezone).toLocaleString(DateTime.DATE_MED);
    return DateTime.fromISO(date).toLocaleString(DateTime.DATE_MED);
  }

  return '(not provided)';
}

/**
 * Formats a start and end ISO date string into a formatted date range (e.g. "Oct 14, 1983 - Oct 16, 1983").
 *
 * @param {string|null|undefined} startDate - Start ISO 8601 date string.
 * @param {string|null|undefined} endDate - End ISO 8601 date string.
 * @param {string} [timezone] - Optional timezone passed to date formatting.
 * @returns {string} Formatted date range string or '(not provided)'.
 */
export function formatDateRange(startDate, endDate, timezone) {
  if (startDate && endDate) {
    return `${formatDate(startDate, timezone)} - ${formatDate(endDate, timezone)}`;
  }

  return '(not provided)';
}

/**
 * Formats an ISO time string in the specified timezone.
 *
 * @param {string|null|undefined} time - ISO 8601 time or date-time string.
 * @param {string} timezone - IANA timezone identifier (e.g. "America/Phoenix").
 * @param {boolean} [includeTimezone=false] - Whether to include the short timezone offset in the formatted string.
 * @returns {string} Formatted time string (e.g. "9:30 AM" or "9:30 AM EDT") or '(not provided)'.
 */
export function formatTime(time, timezone, includeTimezone = false) {
  if (time) {
    return includeTimezone ?
      DateTime.fromISO(time).setZone(timezone).toLocaleString(DateTime.TIME_SIMPLE_WITH_SHORT_OFFSET) :
      DateTime.fromISO(time).setZone(timezone).toLocaleString(DateTime.TIME_SIMPLE);
  }

  return '(not provided)';
}

/**
 * Formats a start and end time string into a formatted time range in the specified timezone.
 *
 * @param {string|null|undefined} startTime - Start ISO 8601 time string.
 * @param {string|null|undefined} endTime - End ISO 8601 time string.
 * @param {string} timezone - IANA timezone identifier (e.g. "America/Phoenix").
 * @returns {string} Formatted time range string (e.g. "9:30 AM - 11:30 AM EDT") or '(not provided)'.
 */
export function formatTimeRange(startTime, endTime, timezone) {
  if (startTime && endTime) {
    return `${formatTime(startTime, timezone)} - ${formatTime(endTime, timezone, true)}`;
  }

  return '(not provided)';
}

/**
 * Number of whole UTC calendar days a date-only value (e.g. a project,
 * offer, or event endDate) has been in the past. 0 means it expires today
 * (in UTC); a negative number means it's still upcoming.
 *
 * The API stores these fields as a plain SQL `date` (no time, no timezone),
 * and the queries that filter on them compare against "now" by truncating
 * to a UTC calendar date. This needs to use that same UTC calendar date,
 * or a resource can show as expired here while still being treated as
 * active by a list that queries the API (or the reverse).
 *
 * @param {string|null|undefined} date - ISO 8601 date string.
 * @returns {number} Number of whole UTC calendar days the date has been in the past, or -Infinity if no date is provided.
 */
export function getDaysExpired(date) {
  if (!date) {
    return -Infinity;
  }

  const endDate = DateTime.fromISO(date, { zone: 'utc' }).startOf('day');
  const today = DateTime.utc().startOf('day');

  return today.diff(endDate, 'days').days;
}

/**
 * A resource is still active through the entirety of its end date (that's
 * the day someone has until), and only counts as expired once "today" (in
 * UTC) is a later calendar date than that — i.e. a strictly positive
 * getDaysExpired, not >= 0. That also matches the API's own `endDate >=
 * now` filter, which only excludes a resource starting the day after.
 *
 * @param {string|null|undefined} date - ISO 8601 date string.
 * @returns {boolean} True if the date has expired (calendar day is in the past in UTC), false otherwise.
 */
export function isExpired(date) {
  return getDaysExpired(date) > 0;
}
